import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { GuidedTexts } from './guidedText';
import { useScanWedge } from '../scanWedge';

// The "Deleted orders" box at the P&P station (Fernando + Henrik 19.10: "we definitely can't trust new people putting
// items on the shelf"). Pieces of an order that left a round go in here, and the WMS keeps the list. Someone with the
// Replenish right empties it: scan a piece (or tap its row) → the screen says which shelf → it is off the list.

interface Item { id: number; order_number: string | null; product_code: string; qty: number; from_location: string | null; reason: string | null }

async function detail(r: Response) {
  const d = await r.json().catch(() => ({} as any));
  return typeof d.detail === 'string' ? d.detail : `HTTP ${r.status}`;
}

export default function DeletedBox({ tx, onBack }: { tx: GuidedTexts; onBack: () => void }) {
  const [items, setItems] = useState<Item[]>([]);
  const [last, setLast] = useState<{ ok: boolean; text: string } | null>(null);
  const load = useCallback(() => authFetch(`${API}/pp2/box`).then(async r => { if (r.ok) setItems((await r.json()).items || []); })
    .catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const shelve = useCallback(async (body: any) => {
    const r = await authFetch(`${API}/pp2/box/shelve`, { method: 'POST', body: JSON.stringify(body) });
    if (!r.ok) { setLast({ ok: false, text: await detail(r) }); return; }
    const d = await r.json();
    setItems(d.items || []);
    setLast({ ok: true, text: tx.shelvedTo(d.shelved.product_code, d.shelved.shelf || '?') });
  }, [tx]);

  useScanWedge(code => shelve({ code }));                  // the station scanner, with or without an Enter (28.10)

  const pieces = items.reduce((n, i) => n + i.qty, 0);
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <span className="text-3xl font-black">{tx.boxScreenTitle}</span>
        <span className="text-gray-400 text-lg">{tx.boxPieces(pieces)}</span>
        <button onClick={onBack} className="ml-auto px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600">{tx.backToStart}</button>
      </div>
      <div className="text-gray-300 text-lg">{tx.boxScreenSub}</div>
      {last && <div className={`text-2xl font-bold ${last.ok ? 'text-green-400' : 'text-red-400'}`}>{last.text}</div>}
      {items.length === 0 && <div className="text-gray-500 text-lg">{tx.boxEmpty}</div>}
      <div className="space-y-2">
        {items.map(i => (
          <div key={i.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-700 bg-[#1a1d27] px-4 py-3">
            <span className="text-white font-semibold text-lg">{i.product_code} ×{i.qty}</span>
            <span className="text-gray-300">→ {i.from_location || tx.noShelf}</span>
            <span className="text-gray-500 text-sm">#{i.order_number || '?'}{i.reason ? ` · ${i.reason}` : ''}</span>
            <button onClick={() => shelve({ item_id: i.id })} className="ml-auto px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white font-semibold">
              {tx.onTheShelf}</button>
          </div>
        ))}
      </div>
    </div>
  );
}
